// System 7 — cyborgs.
//
// The brief: "Human inhabitants with visible augmentation. Standing under awnings, sitting on
// steps, walking through with somewhere to go. They are population, not quest-givers. Nobody talks
// to the player."
//
// Bipeds on the same Leg/Gait/IK foundation as Systems 5 and 6 -- the payoff of building the
// locomotion first. Human knees are k=+1 in the solver's convention (knee juts forward, shin folds
// back). The whole behaviour vocabulary is deliberately small: walkers AVOID you and glance;
// idlers shift their weight and glance. Nothing follows, nothing waits, nothing talks.

import * as THREE from 'three';
import { Leg, Gait, part, solveTwoBone } from './creatures.js';
import { gaitDip } from './gaitcurves.js';
import { rngFor } from './rng.js';
import { cyborgBody, dressBone, cloneParts, cached } from './geometry/rigs.js';

const JACKETS = [0x2b3038, 0x3d3128, 0x1f2c33, 0x40303c, 0x2c3a2e, 0x333136];
const SKINS = [0xa87d5e, 0x8a6248, 0xc09878, 0x6e4c38];

export class Cyborg {
  constructor(scene, world, key, opts) {
    this.world = world;
    this.key = key;
    // driven = the PLAYER's avatar: same rig, same gait, same IK; only the controller differs.
    this.driven = !!opts.driven;
    this.mode = opts.route ? 'walk' : 'idle';
    const r = rngFor(`cy.${key}`);

    this.root = new THREE.Group();
    this.body = new THREE.Group();          // pelvis
    this.root.add(this.body);
    scene.add(this.root);

    // The owner's rigged robot as the visible body. The ENTIRE brain below still runs - routes,
    // gait clock, leg IK, stance height, the reach solver - it just runs on an invisible math
    // skeleton, and the GLB follows its numbers. The driven avatar never gets a factory: its
    // procedural body is load-bearing for the certified FP interactions.
    this.glb = opts.glbBody ? opts.glbBody(scene) : null;

    // Overhaul 3C: per-instance colour comes from vertex-tint at clone time; the six body-type
    // archetypes are the pool. Limb lengths are FIXED so the pool stays a pool -- per-instance
    // segment variation would mint a near-unique archetype per pedestrian.
    const cj = new THREE.Color(r.pick(JACKETS)).multiplyScalar(1.35);
    const ck = new THREE.Color(r.pick(SKINS));
    const tint = {
      cloth: [cj.r, cj.g, cj.b], skin: [ck.r, ck.g, ck.b],
      // Chrome-silver or dark titanium mask, per instance.
      face: r.chance(0.55) ? [0.72, 0.74, 0.78] : [0.30, 0.33, 0.38],
    };
    const UP = 0.42, LO = 0.47;
    // The avatar must be able to CROUCH to reach a dog: a walker's 0.70 rad knee cap allows only
    // ~5 cm of ride drop. Still one-sided, so it still cannot invert -- the cap's actual job.
    const KNEE_HI = opts.driven ? 1.70 : 0.70;
    // Master brief asks a +5 deg standing knee. 0.998 gives ~7: the last degree is inside the
    // solver's own full-extension margin (d <= 0.999 (a+b)), where the knee pole goes singular.
    this.ride = (UP + LO) * 0.998;
    const legSpecs = [
      // Master-brief hinge ranges: thigh swing -17..+23 deg (spec 15/22 + solver margin),
      // knee flexion capped at 40 deg in swing, near-lock allowed at stance.
      { hip: [-0.09, 0, 0], upper: UP, lower: LO, phase: 0.0, knee: 1, leanLo: -0.30, leanHi: 0.42, kneeLo: 0.05, kneeHi: KNEE_HI, roll: true },
      { hip: [0.09, 0, 0], upper: UP, lower: LO, phase: 0.5, knee: 1, leanLo: -0.30, leanHi: 0.42, kneeLo: 0.05, kneeHi: KNEE_HI, roll: true },
    ];
    this.legs = legSpecs.map((L) => new Leg(this.body, L));
    // Stride stays inside the no-slip limit even in the exaggerated mode; only the swing PATH
    // and the arm amplitude change. duty drops to 0.5 so the march reads as a march.
    this.gait = new Gait(opts.exaggerated
      ? { duty: 0.50, stride: 0.78, lift: 0.055, exaggerated: true }
      : { duty: 0.58, stride: r.range(0.58, 0.68), lift: 0.055 });

    // Body type from the six-archetype pool; capsule hulls + plating + lofted clothing bands.
    const type = r.int(0, 5);
    const arch = cached(`cy.arch.${type}`, () => cyborgBody(type, { UP, LO }));
    if (!this.glb) {
      dressBone(this.body, cloneParts(arch.body), 'cyborg', tint);
      dressBone(this.body, cloneParts(cached('cy.neck', () => [{
        geo: new THREE.CapsuleGeometry(0.042, 0.05, 2, 8).translate(0, 0.70, 0), slot: 'skin' }])), 'cyborg', tint);
    }
    this.head = new THREE.Group();
    this.head.position.set(0, 0.72, 0);
    this.body.add(this.head);
    if (!this.glb) dressBone(this.head, cloneParts(arch.head), 'cyborg', tint);
    if (!this.glb) {
      // Optics: glowing camera lenses seated in the helmet's recessed sockets (the sockets are
      // liner rings in the archetype; the emissive lens is per-instance so colour can vary).
      const lensMat = new THREE.MeshStandardMaterial({
        color: 0x0a0c10, emissive: r.chance(0.6) ? 0x35d0ff : 0xff4060, emissiveIntensity: 2.0, roughness: 0.25,
      });
      // Deep in the anatomical sockets, under the brow ridge -- not floating on the surface.
      for (const sx of [-1, 1]) part(this.head, lensMat, 0.020, 0.020, 0.006, sx * 0.026, 0.116, 0.056);
    }
    this.arms = [];
    this.elbows = [];
    for (const sx of [-1, 1]) {
      const sh = new THREE.Group();
      sh.position.set(sx * 0.27, 0.62, 0);
      this.body.add(sh);
      if (!this.glb) dressBone(sh, cloneParts(arch.armU), 'cyborg', tint);
      const el = new THREE.Group();
      el.position.set(0, -0.3, 0);
      sh.add(el);
      if (!this.glb) dressBone(el, cloneParts(arch.armL), 'cyborg', tint);
      this.arms.push(sh);
      this.elbows.push(el);
      if (sx === -1) {
        // Handshake anchor at the palm centre of the RIGHT hand. Facing +Z with up +Y, the
        // body's right is -X -- so this is sx === -1. It was on the +X arm, which is why the
        // player's right hand was meeting a LEFT hand (右手對左手). The FP palm chases this
        // marker, so which arm carries it decides which hand is shaken.
        // With a GLB body the marker lives on the VISIBLE hand bone instead, so the contact
        // loop (and reachErr) measures the hand the player is actually looking at.
        if (this.glb) {
          this.handTip = this.glb.handTip;
        } else {
          this.handTip = new THREE.Object3D();
          this.handTip.position.set(0, -0.34, 0.01);
          el.add(this.handTip);
        }
      }
      // Anatomical rest (polish brief): ~20 degree elbow bend, palm pronated toward the thigh,
      // shoulder hanging slightly inward -- an arm at exactly zero is a pole, and reads as one.
      el.rotation.set(-0.31, -sx * 0.22, 0);   // +18 deg anatomical elbow rest (master brief)
      sh.rotation.set(0.087, 0, -sx * 0.087);  // shoulders: -5 deg pitch-back, 5 deg drop
      sh.userData.restZ = sx * 0.087;
    }
    // Right arm index, derived from where the shoulders actually ARE.
    this.rightArm = this.arms.findIndex((a) => a.position.x < 0);
    if (!this.glb) {
      for (const L of this.legs) {
        dressBone(L.thigh, cloneParts(arch.thigh), 'cyborg', tint);
        dressBone(L.shin, cloneParts(arch.shin), 'cyborg', tint);
        dressBone(L.foot, cloneParts(arch.foot), 'cyborg', tint);
      }
    }

    // --- placement -----------------------------------------------------------
    this.route = opts.route || null;
    this.seg = 0;
    this.u = opts.route ? r.f() : 0;
    this.lateral = r.range(-0.35, 0.35);
    void r.range(1.05, 1.4);   // legacy pace draw, kept so the per-cyborg rng stream holds still
    // MASTER BRIEF: walk cycle at EXACTLY 0.8 Hz. rate = 1/stride cycles per metre, so
    // speed = 0.8 * stride metres/second. Slow, deliberate, zero foot-slide by construction.
    this.speed = this.baseSpeed = 0.8 * this.gait.stride;
    this.pos = new THREE.Vector3();
    this.heading = opts.facing || 0;
    if (this.route) this._place(0);
    else this.pos.set(opts.post[0], 0, opts.post[1]);
    this.sideStep = 0; this.sideTarget = 0;
    this.headYaw = 0; this.headTarget = 0;
    this.noticed = false;
    this.calm = false;
    this.interact = null;
    this._nodT = 0;
    this._reach = 0;
    this._meet = null;
    this.reachLean = 0; this.reachCrouch = 0;
    this._aim = new THREE.Vector3();
    this._aim2 = new THREE.Vector3();
    this.reachErr = 0;
    this.idleT = r.range(0, 9);
    this._inv = new THREE.Matrix4();

    for (const L of this.legs) {
      const s = Math.sin(this.heading), c = Math.cos(this.heading);
      const hx = this.pos.x + L.spec.hip[0] * c, hz = this.pos.z - L.spec.hip[0] * s;
      L.planted.set(hx, world.groundAt(hx, hz, 0), hz);
      L.target.copy(L.planted);
    }
  }

  _place(du) {
    const R = this.route;
    this.u += du;
    while (this.u >= 1) { this.u -= 1; this.seg = (this.seg + 1) % R.length; }
    const a = R[this.seg], b = R[(this.seg + 1) % R.length];
    this.pos.x = a[0] + (b[0] - a[0]) * this.u;
    this.pos.z = a[1] + (b[1] - a[1]) * this.u;
    this.heading = Math.atan2(b[0] - a[0], b[1] - a[1]);
  }

  update(dt, playerPos) {
    const ev = {};
    const dx = playerPos.x - this.pos.x, dz = playerPos.z - this.pos.z;
    const dist = Math.hypot(dx, dz);
    const fwdX = Math.sin(this.heading), fwdZ = Math.cos(this.heading);
    this.idleT += dt;

    // --- reaction: a glance and, for walkers, a step around. Nothing more. ---
    if (!this.calm && dist < 3.2) {
      const bear = Math.atan2(dx, dz) - this.heading;
      this.headTarget = Math.max(-0.85, Math.min(0.85, Math.atan2(Math.sin(bear), Math.cos(bear))));
      if (!this.noticed) { this.noticed = true; ev['cyborg.glance'] = 1; }
    } else {
      this.headTarget = this.mode === 'idle' ? Math.sin(this.idleT * 0.4) * 0.3 : 0;
      if (dist > 4.5) this.noticed = false;
    }
    if (!this.calm && this.mode === 'walk') {
      const ahead = dist < 1.9 && (dx * fwdX + dz * fwdZ) / Math.max(0.01, dist) > 0.4;
      if (ahead && this.sideTarget === 0) {
        // Step around whichever side the player is NOT on.
        const side = dx * fwdZ - dz * fwdX > 0 ? -1 : 1;
        this.sideTarget = side * 0.5;
        ev['cyborg.avoid'] = 1;
      } else if (!ahead && dist > 2.6) this.sideTarget = 0;
    }
    this.sideStep += (this.sideTarget - this.sideStep) * Math.min(1, dt * 3.5);
    this.headYaw += (this.headTarget - this.headYaw) * Math.min(1, dt * 5);
    this.head.rotation.y = this.headYaw;

    // --- travel --------------------------------------------------------------
    if (this.mode === 'walk' && !this.interact) {
      const R = this.route;
      const a = R[this.seg], b = R[(this.seg + 1) % R.length];
      const segLen = Math.hypot(b[0] - a[0], b[1] - a[1]);
      const travel = this.speed * dt;
      this._place(travel / Math.max(0.001, segLen));
      this.pos.x += Math.cos(this.heading) * (this.lateral + this.sideStep);
      this.pos.z -= Math.sin(this.heading) * (this.lateral + this.sideStep);
      this.gait.advance(travel);
    }
    this._animate(dt, dx, dz);
    return ev;
  }

  /**
   * PLAYER-DRIVEN update. The input controller supplies position, heading and speed; everything
   * downstream -- gait clock, arm swing, pelvis, foot IK, interaction arm -- is the SAME code the
   * NPCs run. That shared path is the point of the unification: a locomotion bug can no longer
   * exist for one and not the other.
   */
  drive(dt, pos, heading, speed) {
    this.idleT += dt;
    const moving = speed > 0.12 && !this.interact;
    this.mode = moving ? 'walk' : 'idle';
    this.pos.x = pos.x;
    this.pos.z = pos.z;
    this.heading = heading;
    this.speed = Math.max(0.05, speed);
    if (moving) this.gait.advance(speed * dt);
    this._animate(dt, 0, 0);
  }

  /** Everything that is not "where do I want to be": gait pose, carriage, IK, interaction arm. */
  _animate(dt, dx, dz) {
    if (this.mode === 'walk' && !this.interact) {
      // MASTER-BRIEF walking kinematics. Contralateral (+0.5 keeps each arm against its own-
      // side leg); asymmetric swing +25 deg forward / -18 back (sw = -1 is "arm forward": the
      // same-side leg is mid-stance there); the elbow flexes 18 -> 28 deg as the arm comes
      // forward. Negative rotation.x is forward on these bones.
      for (let i = 0; i < 2; i++) {
        // The +0.5 phase offset IS the exact 180 degrees of contralateral opposition: arm i is
        // driven by the same clock as leg i, half a cycle away, so the right arm peaks forward on
        // the frame the left leg peaks forward. One timer, one offset, no second oscillator to
        // drift out of sync.
        const sw = Math.sin((this.gait.t + this.legs[i].phase + 0.5) * Math.PI * 2);
        if (this.gait.exaggerated) {
          // Goose-step: the forward arm comes up to horizontal (-pi/2) and the elbow straightens;
          // the rearward swing stays short, as in a parade march rather than a sprint.
          this.arms[i].rotation.x = sw < 0 ? sw * 1.56 : sw * 0.55;
          this.elbows[i].rotation.x = -0.10 - 0.30 * Math.max(0, sw);
        } else {
          this.arms[i].rotation.x = -0.061 + 0.375 * sw;
          this.elbows[i].rotation.x = -(0.31 + 0.18 * Math.max(0, -sw));
        }
      }
      // Pelvis: sway 2.8 cm, roll 3.5 deg, yaw 4 deg, and the -0.03 m weight-bearing dip.
      this.body.position.x = Math.sin(this.gait.t * Math.PI * 2) * 0.028;
      this.body.rotation.z = Math.sin(this.gait.t * Math.PI * 2) * 0.061;
      this.body.rotation.y = Math.sin(this.gait.t * Math.PI * 2) * 0.070;
      this.body.position.y = -gaitDip(this.gait.t) * 0.03;
    } else {
      // Idle: weight shift + breathing sway; arms drift a few milliradians so nothing is a pole.
      this.body.position.x = Math.sin(this.idleT * 0.55) * 0.03;
      this.body.position.y = Math.sin(this.idleT * 1.5) * 0.01;
      for (let i = 0; i < 2; i++) {
        this.arms[i].rotation.x = 0.087 + Math.sin(this.idleT * 1.5 + i * 2.1) * 0.03;
        this.elbows[i].rotation.x = -0.31 + Math.sin(this.idleT * 1.3 + i) * 0.02;
      }
    }

    // --- carriage ------------------------------------------------------------
    const ga = (x, z) => this.world.groundAt(x, z, 0);
    if (this.mode === 'walk' && !this.interact) {
      for (const L of this.legs) this.gait.footTarget(L, this.pos, this.heading, this.speed, ga, L.target);
    } else {
      for (const L of this.legs) {
        const c = Math.cos(this.heading), s = Math.sin(this.heading);
        const hx = this.pos.x + L.spec.hip[0] * c, hz = this.pos.z - L.spec.hip[0] * s;
        L.target.set(hx, ga(hx, hz), hz);
        L.contact = true;
        L._roll = 0;   // standing still has no heel strike to roll through
      }
    }
    // E-INTERACTION (FP brief): stop, square to the player, raise the right hand to meet the
    // player's palm, nod. The FP hand chases this.handTip, so contact is mutual by construction.
    if (this.interact) {
      const I = this.interact;
      I.t += dt;
      const u = Math.min(1, I.t / 0.45), k = u * u * (3 - 2 * u);
      if (!this.driven) {
        // An NPC squares up to you. The player's avatar does NOT get its heading rewritten --
        // that belongs to the person holding the mouse.
        let w = Math.atan2(dx, dz) - this.heading;
        w = Math.atan2(Math.sin(w), Math.cos(w));
        this.heading += w * Math.min(1, dt * 4);
      }
      // The RIGHT arm reaches for the clasp point with real IK (see _aimRightArm), blended in
      // over k. A posed pair of fixed rotations could only ever be correct at one stand-off
      // distance; aiming at the actual point is correct at all of them, and it is what makes the
      // player's palm and this hand meet instead of merely coming close.
      this._reach = k;
      this._meet = I.meet || null;
      this.head.rotation.x = -0.13 * Math.sin(Math.min(I.t * 4.2, Math.PI * 2)) * k;   // two dips
      this._nodT = 1;
    } else if (this._nodT > 0) {
      this._nodT = Math.max(0, this._nodT - dt * 4);
      this.head.rotation.x *= this._nodT;
    }
    // REACHING DOWN. A 1.8 m biped cannot touch a dog's head at arm's length -- the shoulder is
    // ~1.4 m up and the head ~0.75 m, so a 0.64 m arm falls half a metre short no matter how the
    // solver is asked. People solve this by bending, so the avatar does: pitch at the pelvis and
    // drop the ride until the shoulder is within reach of the point. The leg IK keeps the feet on
    // their existing targets throughout, so the crouch is real weight-bearing, not a sink.
    let wantLean = 0, wantCrouch = 0;
    if (this.interact && this.interact.meet) {
      const M = this.interact.meet;
      const dh = Math.hypot(M.x - this.pos.x, M.z - this.pos.z);
      const dy = M.y - (this.pos.y + 0.62);
      // Open loop, deliberately. A closed loop on the measured residual was tried and oscillated:
      // it relaxes the moment it touches, loses contact, leans back in. The estimate below is
      // approximate but STABLE, and the contact fence measures the truth either way.
      const need = Math.hypot(dh, dy) - 0.54;
      if (need > 0) {
        wantLean = Math.min(0.62, need * 2.1);
        // Crouch only for targets BELOW the shoulder: dropping the hips to reach something at
        // chest height moves the shoulder AWAY from it.
        wantCrouch = dy < 0 ? Math.min(0.22, need * 0.70) : 0;
      }
    }
    this.reachLean += (wantLean - this.reachLean) * Math.min(1, dt * 9);
    this.reachCrouch += (wantCrouch - this.reachCrouch) * Math.min(1, dt * 9);
    if (this.reachLean > 0.002) this.body.rotation.x = this.reachLean;
    else this.body.rotation.x = 0;

    // STANCE HEIGHT COMES FROM THE FEET THAT ARE CARRYING THE BODY.
    //
    // This used to average ALL foot targets. That is indistinguishable from correct in a normal
    // walk, where the swing foot clears the ground by ~5 cm -- and badly wrong the moment a leg
    // lifts high: the goose-step test measured the "planted" foot floating 0.44-0.67 m in the air
    // with 0.6 m of IK error, because a swing foot raised to hip height dragged the average, and
    // with it the pelvis, half a leg upward. The body stands on what is in CONTACT; a lifted leg
    // contributes nothing to how tall you are.
    // STANCE HEIGHT.
    //
    // Normal walk: the average of the foot targets, unchanged. In a walk the swing foot clears the
    // ground by ~5 cm, so the average is within a couple of centimetres of the planted foot and
    // the result is stable.
    //
    // Exaggerated gait: the average is CATASTROPHICALLY wrong, because a foot raised to hip height
    // drags the pelvis half a leg upward -- measured as a "planted" foot floating 0.44-0.67 m with
    // 0.6 m of IK error. There, height comes only from feet in CONTACT.
    //
    // KNOWN LIMITATION, measured and deliberately NOT fixed here: a constant ride cannot be
    // geometrically correct at this scale. The leg is 0.889 m and the stance foot travels +/-0.34 m
    // fore/aft of the hip, so a pelvis 0.888 m up demands sqrt(0.888^2 + 0.34^2) = 0.951 m of
    // reach and the solver clamps -- which is the ~0.073 m of standing IK error the cyborg contact
    // sheet has always carried, previously read as the thigh stop binding. The physically correct
    // model is pelvis height DERIVED from the stance leg (sqrt(legLen^2 - offset^2) above the
    // planted foot), which also produces the walk's rise-and-fall for free. Implemented and
    // measured: it makes things WORSE here (0.177 m) because this.pos.y chases standY through a
    // lerp and a fast-varying target lags. Doing it properly means re-tuning ride, the dip and the
    // lerp together and re-baselining every contact sheet -- a deliberate change, not a drive-by.
    let sumY = 0, nC = 0, lowest = Infinity;
    for (const L of this.legs) {
      lowest = Math.min(lowest, L.target.y);
      if (L.contact) { sumY += L.target.y; nC++; }
    }
    const standY = (this.gait.exaggerated
      ? (nC ? sumY / nC : lowest)
      : (L0(this.legs))) + this.ride - this.reachCrouch;
    this.pos.y += (standY - this.pos.y) * Math.min(1, dt * 8);
    this.root.position.copy(this.pos);
    this.root.rotation.y = this.heading;

    this.root.updateMatrixWorld(true);
    this._inv.copy(this.body.matrixWorld).invert();
    for (const L of this.legs) L.solve(this._inv);
    if (this._reach > 0) {
      this._aimRightArm(this._meet, this._reach);
      if (!this.interact) this._reach = Math.max(0, this._reach - dt * 4);
    }
    // The GLB body reads the brain LAST, when position, heading, glance and reach are all final.
    if (this.glb) this.glb.update(dt, this);
  }

  /**
   * Point the ANATOMICAL RIGHT arm's chain at a world point, two-bone analytic, blended by k.
   * Same solver the legs use -- shoulder -> elbow -> hand is a leg with the elbow folding the
   * other way (kk = -1). Runs after the body matrix is final so the target converts cleanly.
   */
  _aimRightArm(meetWorld, k) {
    const sh = this.arms[this.rightArm], el = this.elbows[this.rightArm];
    const UPPER = 0.30, LOWER = 0.34, kk = -1;
    if (meetWorld) {
      this.body.updateMatrixWorld(true);
      const local = this.body.worldToLocal(this._aim.copy(meetWorld)).sub(sh.position);
      const { root, knee } = solveTwoBone(UPPER, LOWER, local.length());
      const yaw = Math.atan2(local.x, local.z);
      const pitch = Math.atan2(Math.hypot(local.x, local.z), -local.y);
      sh.rotation.set(0, 0, 0);
      sh.rotateY(yaw);
      sh.rotateX(-(pitch + kk * root));
      el.rotation.x = kk * knee;
      // Feed the closed loop: how far the hand ACTUALLY is from where it was sent.
      if (this.handTip) {
        this.handTip.updateWorldMatrix(true, false);
        this.reachErr = this.handTip.getWorldPosition(this._aim2).distanceTo(meetWorld);
      }
      // Blend from the rest pose so the arm rises instead of snapping to the clasp.
      if (k < 1) {
        sh.rotation.x = sh.rotation.x * k + 0.087 * (1 - k);
        sh.rotation.y *= k;
        sh.rotation.z = sh.rotation.z * k + -this.arms[this.rightArm].userData.restZ * (1 - k);
        el.rotation.x = el.rotation.x * k + -0.31 * (1 - k);
      }
      sh.updateMatrixWorld(true);
    }
  }

  contactCount() { return this.legs.reduce((n, L) => n + (L.contact ? 1 : 0), 0); }
}

/** The shipping walk's stance reference: the mean of all foot targets. */
function L0(legs) {
  let s = 0;
  for (const L of legs) s += L.target.y;
  return s / legs.length;
}

/** The population: five walkers with somewhere to be, three idlers with nowhere to be yet. */
export function makeCyborgs(scene, world, glbBody = null) {
  const out = [];
  const west = [[-6.7, -30], [-6.7, 28]];
  const east = [[6.6, 28], [6.6, -30]];
  for (let i = 0; i < 3; i++) out.push(new Cyborg(scene, world, `w${i}`, { route: [...west, ...[...west].reverse()], glbBody }));
  for (let i = 0; i < 2; i++) out.push(new Cyborg(scene, world, `e${i}`, { route: [...east, ...[...east].reverse()], glbBody }));
  out.push(new Cyborg(scene, world, 'i0', { post: [-7.2, -3.0], facing: 1.7, glbBody }));
  out.push(new Cyborg(scene, world, 'i1', { post: [9.6, -20.6], facing: -1.4, glbBody }));
  out.push(new Cyborg(scene, world, 'i2', { post: [-7.3, -13.4], facing: 0.4, glbBody }));
  return out;
}
